import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useVocabulary } from './contexts/VocabularyContext';
import { VocabularyTabs, StudentSelector } from './components/VocabularyTabs';
import QuizTitleEditor from './components/QuizTitleEditor';
import StudyDatePicker from './components/StudyDatePicker';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

// 백엔드 vocabularyQuizService.MAX_QUESTIONS 와 맞춘다
const MAX_QUESTIONS = 1000;

const VocabularyQuiz = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dates, fetchDates, quizApi, studentId } = useVocabulary();

  const [title, setTitle] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [count, setCount] = useState(20);
  // 문항 수를 정하지 않고 선택 범위의 단어를 전부 낸다
  const [allWords, setAllWords] = useState(false);
  const [direction, setDirection] = useState('word_to_meaning');
  const [format, setFormat] = useState('multiple_choice');
  const [onlyImportant, setOnlyImportant] = useState(false);
  const [preferHigh, setPreferHigh] = useState(true);
  const [quizzes, setQuizzes] = useState([]);
  const [busy, setBusy] = useState(false);
  // 중복을 뺀 실제 출제 가능 문항 수 (화면의 단어 수 합계와 다를 수 있다)
  const [pool, setPool] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  // 목록에서 겹친 단어를 펼쳐 둔 시험 (한 번에 하나만 펼친다)
  const [openDupQuiz, setOpenDupQuiz] = useState(null);

  const targetUserId = studentId || undefined;

  useEffect(() => { fetchDates(targetUserId); }, [fetchDates, targetUserId]);

  const loadQuizzes = useCallback(async () => {
    const data = await quizApi.listQuizzes({ limit: 20 }, targetUserId);
    if (data.success) setQuizzes(data.quizzes || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]);

  useEffect(() => { loadQuizzes(); }, [loadQuizzes]);

  const selectedWordCount = dates
    .filter(d => selectedDates.includes(d.study_date))
    .reduce((sum, d) => sum + d.word_count, 0);

  /*
   * 같은 단어가 여러 날짜에 있으면 한 문항으로 합쳐지므로, 날짜별 단어 수 합계가
   * 곧 문항 수가 아니다. 실제 개수는 서버만 알 수 있어 선택이 바뀔 때마다 물어본다.
   * 기간 프리셋으로 여러 날짜가 한꺼번에 바뀌므로 디바운스한다.
   */
  useEffect(() => {
    if (selectedDates.length === 0) {
      setPool(null);
      return;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      const data = await quizApi.getPoolSize({
        dates: [...selectedDates].sort().join(','),
        // 범위 전체 출제일 때는 주요 단어 필터를 쓰지 않는다 (아래 전송 로직과 같은 규칙)
        only_important: !allWords && onlyImportant ? '1' : '0',
      }, targetUserId);
      if (alive && data.success) setPool(data);
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates, onlyImportant, allWords, targetUserId]);

  // 실제 출제 가능 수를 알면 그것을 쓰고, 아직 모르면 단어 수 합계로 대체한다
  const availableCount = pool ? pool.pool_size : selectedWordCount;

  /**
   * 이름을 비워두면 출제 범위로 제목을 만든다.
   * 목록이 '제목 없는 시험'으로만 차면 어느 시험인지 구분할 수 없다.
   */
  const defaultTitle = useMemo(() => {
    if (selectedDates.length === 0) return '';
    const sorted = [...selectedDates].sort();
    const range = sorted.length === 1
      ? sorted[0]
      : `${sorted[0]} ~ ${sorted[sorted.length - 1]}`;
    return t('vocabulary.quiz.defaultTitle', { range, count: Number(count) || 0 });
  }, [selectedDates, count, t]);

  const handleCreate = async () => {
    if (selectedDates.length === 0) {
      toast.error(t('vocabulary.quiz.selectDates'));
      return;
    }
    setBusy(true);
    try {
      const data = await quizApi.createQuiz({
        dates: selectedDates,
        title: title.trim() || defaultTitle,
        all_words: allWords,
        question_count: Number(count),
        direction,
        answer_format: format,
        // 범위 전체 출제면 필터·정렬은 의미가 없다 (UI에서도 비활성이다)
        only_important: allWords ? false : onlyImportant,
        prefer_high_frequency: allWords ? true : preferHigh,
      }, targetUserId);

      if (!data.success) {
        toast.error(t('vocabulary.quiz.createFailed'));
        return;
      }
      // 요청보다 적게 나왔거나 객관식이 주관식으로 바뀌었으면 조용히 넘기지 않는다
      if (data.duplicate_count > 0) {
        toast.info(t('vocabulary.quiz.duplicatesRemoved', { count: data.duplicate_count }));
      }
      if (data.capped_at_max) {
        toast.info(t('vocabulary.quiz.cappedAtMax', { count: data.max_questions }));
      } else if (data.truncated) {
        toast.info(t('vocabulary.quiz.truncated', { count: data.question_count }));
      }
      if (data.downgraded_to_short_answer > 0) {
        toast.info(t('vocabulary.quiz.downgraded', { count: data.downgraded_to_short_answer }));
      }

      /*
       * 관찰자는 남의 시험을 대신 풀 수 없다 (서버가 응시를 막는다).
       * 응시 화면으로 보내면 열리지도 않으므로, 목록에 남아 출제됐음을 알린다.
       */
      if (targetUserId) {
        toast.success(t('vocabulary.quiz.assigned', { count: data.question_count }));
        loadQuizzes();
        return;
      }
      navigate(`/vocabulary/quiz/${data.quiz_id}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleQuizDups = (quizId) => {
    setOpenDupQuiz(prev => (prev === quizId ? null : quizId));
  };

  const handleRename = async (quizId, nextTitle) => {
    const data = await quizApi.updateQuiz(quizId, { title: nextTitle }, targetUserId);
    if (!data.success) {
      toast.error(t('error.save'));
      return false;
    }
    // 목록을 다시 부르지 않고 해당 항목만 갈아끼운다
    setQuizzes(prev => prev.map(q =>
      q.quiz_id === quizId ? { ...q, title: data.title } : q
    ));
    return true;
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm(t('vocabulary.quiz.deleteConfirm'))) return;
    const data = await quizApi.deleteQuiz(quizId, targetUserId);
    if (data.success) loadQuizzes();
  };

  const statusLabel = (q) => {
    if (q.status === 'completed') return `${q.score}${t('vocabulary.quiz.scoreSuffix')}`;
    if (q.status === 'in_progress') return t('vocabulary.quiz.inProgress');
    return t('vocabulary.quiz.notStarted');
  };

  return (
    <div className="vocab-container">
      <div className="vocab-header">
        <h2>{t('vocabulary.quiz.title')}</h2>
        <div className="vocab-header-actions">
          <StudentSelector />
        </div>
      </div>

      <VocabularyTabs />

      <div className="quiz-setup">
        <h3>{t('vocabulary.quiz.newQuiz')}</h3>

        <label className="quiz-field quiz-title-field">
          <span>{t('vocabulary.quiz.quizName')}</span>
          <input
            type="text"
            maxLength={100}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={defaultTitle || t('vocabulary.quiz.quizNamePlaceholder')}
          />
        </label>

        {/* 날짜 선택기는 단어장과 같은 것을 쓴다 (components/StudyDatePicker.js) */}
        <StudyDatePicker
          dates={dates}
          selected={selectedDates}
          onChange={setSelectedDates}
          wordCount={availableCount}
          summaryExtra={pool && pool.duplicate_count > 0 ? (
            <em className="quiz-pool-note">
              <button
                type="button"
                className="quiz-pool-toggle"
                onClick={() => setDupOpen(!dupOpen)}
              >
                <span aria-hidden="true">{dupOpen ? '▾' : '▸'}</span>
                {t('vocabulary.quiz.poolAfterDedupe', {
                  raw: pool.raw_size, duplicates: pool.duplicate_count,
                })}
              </button>

              {dupOpen && (
                <ul className="quiz-pool-dups">
                  {pool.duplicate_words.map(d => (
                    <li key={d.word}>
                      <strong>{d.word}</strong>
                      <span>{d.dates.join(' · ')}</span>
                    </li>
                  ))}
                  {pool.duplicate_group_count > pool.duplicate_words.length && (
                    <li className="quiz-pool-more">
                      {t('vocabulary.quiz.andMore', {
                        count: pool.duplicate_group_count - pool.duplicate_words.length,
                      })}
                    </li>
                  )}
                </ul>
              )}
            </em>
          ) : null}
        />

        <div className="quiz-options">
          <label className="quiz-field">
            <span>{t('vocabulary.quiz.questionCount')}</span>
            <input
              type="number" min="1" max={MAX_QUESTIONS} value={allWords ? '' : count}
              disabled={allWords}
              placeholder={allWords ? t('vocabulary.quiz.allWordsShort', { count: availableCount }) : ''}
              onChange={(e) => setCount(e.target.value)}
            />
          </label>

          <label className="quiz-field">
            <span>{t('vocabulary.quiz.direction')}</span>
            <select value={direction} onChange={(e) => setDirection(e.target.value)}>
              <option value="word_to_meaning">{t('vocabulary.quiz.wordToMeaning')}</option>
              <option value="meaning_to_word">{t('vocabulary.quiz.meaningToWord')}</option>
              <option value="mixed">{t('vocabulary.quiz.mixed')}</option>
            </select>
          </label>

          <label className="quiz-field">
            <span>{t('vocabulary.quiz.format')}</span>
            <select value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="multiple_choice">{t('vocabulary.quiz.multipleChoice')}</option>
              <option value="short_answer">{t('vocabulary.quiz.shortAnswer')}</option>
              <option value="mixed">{t('vocabulary.quiz.mixed')}</option>
            </select>
          </label>
        </div>

        {/*
          * 범위 전체 출제는 필터·정렬 옵션과 함께 쓸 여지가 없다.
          * 어차피 범위의 모든 단어가 나오므로 주요 단어 필터도, 고빈도 우선순위도 의미가 없다.
          */}
        <div className="quiz-toggles">
          <label className="vocab-checkbox">
            <input type="checkbox" checked={allWords} onChange={(e) => setAllWords(e.target.checked)} />
            {t('vocabulary.quiz.allWords')}
          </label>
          <label className={`vocab-checkbox${allWords ? ' disabled' : ''}`}>
            <input
              type="checkbox" checked={onlyImportant} disabled={allWords}
              onChange={(e) => setOnlyImportant(e.target.checked)}
            />
            {t('vocabulary.onlyImportant')}
          </label>
          <label className={`vocab-checkbox${allWords ? ' disabled' : ''}`}>
            <input
              type="checkbox" checked={preferHigh} disabled={allWords}
              onChange={(e) => setPreferHigh(e.target.checked)}
            />
            {t('vocabulary.quiz.preferHighFrequency')}
          </label>
        </div>

        <button className="vocab-btn vocab-btn-primary quiz-start-btn" onClick={handleCreate} disabled={busy}>
          {busy ? t('vocabulary.loading') : t('vocabulary.quiz.create')}
        </button>
      </div>

      <div className="quiz-history">
        <h3>{t('vocabulary.quiz.history')}</h3>
        {quizzes.length === 0 && <p className="vocab-empty">{t('vocabulary.quiz.noQuizzes')}</p>}
        <ul className="quiz-list">
          {quizzes.map(q => (
            <li key={q.quiz_id} className={`quiz-item status-${q.status}`}>
              <div className="quiz-item-main">
                <QuizTitleEditor
                  title={q.title}
                  onSave={(next) => handleRename(q.quiz_id, next)}
                  className="quiz-item-title"
                />
                <span className="quiz-item-meta">
                  {q.question_count}{t('vocabulary.quiz.questionsSuffix')}
                  {q.source_dates.length > 0 && ` · ${q.source_dates.join(', ')}`}
                </span>

                {/* 이 시험 범위에 겹친 단어가 있으면 여기서 바로 확인할 수 있게 한다 */}
                {q.duplicate_group_count > 0 && (
                  <span className="quiz-pool-note">
                    <button
                      type="button"
                      className="quiz-pool-toggle"
                      onClick={() => toggleQuizDups(q.quiz_id)}
                    >
                      <span aria-hidden="true">{openDupQuiz === q.quiz_id ? '▾' : '▸'}</span>
                      {t('vocabulary.quiz.quizDuplicates', { count: q.duplicate_group_count })}
                    </button>

                    {openDupQuiz === q.quiz_id && (
                      <ul className="quiz-pool-dups">
                        {q.duplicate_words.map(d => (
                          <li key={d.word}>
                            <strong>{d.word}</strong>
                            <span>{d.dates.join(' · ')}</span>
                          </li>
                        ))}
                        {q.duplicate_group_count > q.duplicate_words.length && (
                          <li className="quiz-pool-more">
                            {t('vocabulary.quiz.andMore', {
                              count: q.duplicate_group_count - q.duplicate_words.length,
                            })}
                          </li>
                        )}
                      </ul>
                    )}
                  </span>
                )}
              </div>
              <span className={`quiz-item-score ${q.status}`}>{statusLabel(q)}</span>
              <div className="quiz-item-actions">
                {q.status === 'completed' && (
                  <>
                    <button className="vocab-btn" onClick={() => navigate(`/vocabulary/quiz/${q.quiz_id}/result`)}>
                      {t('vocabulary.quiz.viewResult')}
                    </button>
                    {/* 채점을 잘못했을 때 되돌릴 수 있어야 한다 */}
                    {!targetUserId && (
                      <button className="vocab-btn" onClick={() => navigate(`/vocabulary/quiz/${q.quiz_id}/sheet`)}>
                        {t('vocabulary.quiz.regradeTitle')}
                      </button>
                    )}
                  </>
                )}
                {/*
                  * 응시는 본인만 가능하다 (서버가 막는다).
                  * 관찰자에게 풀기 버튼을 보여주면 눌러도 열리지 않는다.
                  */}
                {q.status !== 'completed' && !targetUserId && (
                  <>
                    <button className="vocab-btn vocab-btn-primary" onClick={() => navigate(`/vocabulary/quiz/${q.quiz_id}`)}>
                      {q.status === 'in_progress' ? t('vocabulary.quiz.resume') : t('vocabulary.quiz.start')}
                    </button>
                    {/* 종이 시험지를 뽑아 푼 뒤 답만 옮겨 넣는 경로 */}
                    <button className="vocab-btn" onClick={() => navigate(`/vocabulary/quiz/${q.quiz_id}/sheet`)}>
                      {t('vocabulary.quiz.enterAnswers')}
                    </button>
                  </>
                )}
                <button className="vocab-btn" onClick={() => navigate(`/vocabulary/quiz/${q.quiz_id}/paper`)}>
                  {t('vocabulary.quiz.paper')}
                </button>
                <button className="vocab-icon-btn" onClick={() => handleDelete(q.quiz_id)} title={t('delete')}>
                  &#128465;
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default VocabularyQuiz;
